import {
  Search,
  Sparkles,
  Image,
  Frame,
  ShoppingBag,
  Shirt,
  ArrowRight,
  Lightbulb,
  Layers,
  Palette,
  BookTemplate,
  DollarSign,
  RefreshCw,
  Trash2,
  Settings,
  Zap,
  Bot,
  Bell,
  TrendingUp,
  Globe,
  Clock,
} from 'lucide-react';

export default function Help() {
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Help & Documentation</h1>
      <p className="text-gray-600 mb-8">
        Learn how EtsyFlow AI works and how to create successful Etsy listings.
      </p>

      {/* Overview */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-etsy-orange" />
          Overview
        </h2>
        <p className="text-gray-600 mb-4">
          EtsyFlow AI is an AI-powered tool for creating and managing Etsy listings. It automates
          the entire process from niche research to product generation and publishing.
        </p>
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">Supported Product Types</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Image className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Digital Downloads</h4>
                <p className="text-sm text-gray-600">
                  Wall art, prints, posters - customers download and print themselves
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shirt className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Print-on-Demand (Printify)</h4>
                <p className="text-sm text-gray-600">
                  T-shirts, hoodies, sweatshirts - printed and shipped by Printify
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pipeline Flow */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5 text-etsy-orange" />
          The Pipeline: How Listings Are Created
        </h2>
        <p className="text-gray-600 mb-6">
          When you create a listing, it goes through multiple AI-powered phases:
        </p>

        <div className="space-y-6">
          {/* Phase 1 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-etsy-orange text-white rounded-full flex items-center justify-center font-bold">
              1
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Content Generation</h3>
              <p className="text-sm text-gray-600 mb-2">
                AI generates 5 creative concepts for your niche, then creates:
              </p>
              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                <li>SEO-optimized title (max 140 characters)</li>
                <li>Compelling product description</li>
                <li>13 relevant tags for Etsy search</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="w-5 h-5 text-gray-400" />
          </div>

          {/* Phase 2 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-etsy-orange text-white rounded-full flex items-center justify-center font-bold">
              2
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Design Generation</h3>
              <p className="text-sm text-gray-600 mb-2">
                AI creates the primary product image based on the concept:
              </p>
              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                <li>
                  <strong>Wall Art:</strong> Complete printable artwork (1024x1024)
                </li>
                <li>
                  <strong>Apparel:</strong> Flat graphic design suitable for printing on garments
                </li>
              </ul>
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="w-5 h-5 text-gray-400" />
          </div>

          {/* Phase 3 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-etsy-orange text-white rounded-full flex items-center justify-center font-bold">
              3
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Mockup Generation (Wall Art Only)</h3>
              <p className="text-sm text-gray-600 mb-2">
                For digital downloads, AI generates lifestyle mockups showing the art in context:
              </p>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <div className="text-xs text-center p-2 bg-gray-50 rounded">
                  <Frame className="w-4 h-4 mx-auto mb-1 text-gray-500" />
                  White Frame
                </div>
                <div className="text-xs text-center p-2 bg-gray-50 rounded">
                  <Frame className="w-4 h-4 mx-auto mb-1 text-gray-500" />
                  Living Room
                </div>
                <div className="text-xs text-center p-2 bg-gray-50 rounded">
                  <Frame className="w-4 h-4 mx-auto mb-1 text-gray-500" />
                  Minimal Room
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="w-5 h-5 text-gray-400" />
          </div>

          {/* Phase 4 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-etsy-orange text-white rounded-full flex items-center justify-center font-bold">
              4
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Ready for Publishing</h3>
              <p className="text-sm text-gray-600">
                Review and edit the listing, then sync to Etsy or Printify.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* POD Generation Settings */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Shirt className="w-5 h-5 text-etsy-orange" />
          POD Generation Settings
        </h2>
        <p className="text-gray-600 mb-4">
          Before generating listings, configure your Print-on-Demand settings to get product
          concepts tailored to specific product types.
        </p>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Recipe Selector</h3>
            <p className="text-sm text-gray-600 mb-2">
              Choose a saved recipe to customize the AI's brainstorming behavior:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Custom prompt templates for specific design styles</li>
              <li>Pre-configured AI model selection</li>
              <li>Negative prompts to avoid unwanted elements</li>
              <li>Leave empty to use default prompts</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Product Type Filter</h3>
            <p className="text-sm text-gray-600 mb-2">
              Filter generated concepts by POD product category:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
              <div className="text-xs text-center p-2 bg-white rounded border">T-Shirts</div>
              <div className="text-xs text-center p-2 bg-white rounded border">Hoodies</div>
              <div className="text-xs text-center p-2 bg-white rounded border">Sweatshirts</div>
              <div className="text-xs text-center p-2 bg-white rounded border">Mugs</div>
              <div className="text-xs text-center p-2 bg-white rounded border">Posters</div>
              <div className="text-xs text-center p-2 bg-white rounded border">Canvas</div>
              <div className="text-xs text-center p-2 bg-white rounded border">Phone Cases</div>
              <div className="text-xs text-center p-2 bg-white rounded border">Tote Bags</div>
              <div className="text-xs text-center p-2 bg-white rounded border">Stickers</div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              POD Design Tips
            </h3>
            <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
              <li>Designs should work well as flat prints on products</li>
              <li>Consider print area dimensions for each product type</li>
              <li>Bold, simple designs often work best for apparel</li>
              <li>Wall art can be more detailed and complex</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Auto-Scout */}
      <section className="card mb-8 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bot className="w-5 h-5 text-purple-600" />
          Auto-Scout (Automated Trend Discovery)
        </h2>
        <p className="text-gray-600 mb-4">
          EtsyFlow automatically discovers trending niches daily using AI-powered trend analysis
          from multiple sources. No manual research needed!
        </p>

        <div className="space-y-4">
          <div className="bg-white/80 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-600" />
              How It Works
            </h3>
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-2">
              <li>Daily cron job runs at 6 AM UTC</li>
              <li>Fetches trending topics from Pinterest, TikTok, and Google Trends</li>
              <li>Filters for POD-relevant keywords (apparel, quotes, aesthetics)</li>
              <li>Scores candidates based on multi-source correlation</li>
              <li>Runs AI analysis on top 10 discoveries</li>
              <li>Notifies you via email when high-scoring niches are found</li>
            </ol>
          </div>

          <div className="bg-white/80 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-600" />
              Data Sources
            </h3>
            <div className="grid md:grid-cols-3 gap-3 mt-2">
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">📌</span>
                  <span className="font-medium text-red-700">Pinterest</span>
                </div>
                <p className="text-xs text-red-600">
                  Design trends, aesthetics, visual styles, mood boards
                </p>
              </div>
              <div className="bg-pink-50 rounded-lg p-3 border border-pink-100">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">🎵</span>
                  <span className="font-medium text-pink-700">TikTok</span>
                </div>
                <p className="text-xs text-pink-600">
                  Viral hashtags, Gen-Z trends, emerging aesthetics
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">📈</span>
                  <span className="font-medium text-blue-700">Google Trends</span>
                </div>
                <p className="text-xs text-blue-600">
                  Search interest, rising queries, seasonality data
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/80 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              Multi-Source Correlation Scoring
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              Niches found in multiple sources get bonus scoring:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>
                <span className="font-medium text-orange-600">🔥 Triple-source match (+15%):</span>{' '}
                Found on Pinterest, TikTok, AND Google
              </li>
              <li>
                <span className="font-medium text-blue-600">Pinterest + Google (+12%):</span> Design
                trend with search demand
              </li>
              <li>
                <span className="font-medium text-pink-600">TikTok + Google (+12%):</span> Viral
                trend with search interest
              </li>
              <li>
                <span className="font-medium text-green-600">Rising trend bonus (+8%):</span> Google
                shows increasing interest
              </li>
            </ul>
          </div>

          <div className="bg-white/80 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <Bell className="w-4 h-4 text-yellow-600" />
              Notifications
            </h3>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>
                <strong>Email alerts:</strong> Get notified when hot niches are discovered
              </li>
              <li>
                <strong>Dashboard review queue:</strong> Auto-discovered niches appear in review
                queue
              </li>
              <li>
                <strong>One-click actions:</strong> Approve, ignore, or generate directly from queue
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Research */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-etsy-orange" />
          Niche Research
        </h2>
        <p className="text-gray-600 mb-4">
          Before creating listings, research profitable niches to target.
        </p>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">How Research Works</h3>
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-2">
              <li>Enter a keyword or niche idea (e.g., "minimalist botanical prints")</li>
              <li>AI analyzes the market potential and competition</li>
              <li>Get a research score (0-100) based on demand vs. competition</li>
              <li>Save promising niches to use when creating listings</li>
            </ol>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Tips for Good Niches
            </h3>
            <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
              <li>Be specific: "boho nursery wall art" beats "wall art"</li>
              <li>Target emotions or occasions: "grief loss memorial", "new baby gift"</li>
              <li>Combine styles: "minimalist botanical", "vintage retro travel"</li>
              <li>Consider seasons: "fall autumn farmhouse", "christmas holiday"</li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Per-Niche Settings
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              Each niche can have its own product type and recipe configuration:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>
                <strong>Product Type:</strong> Set POD type (t-shirt, hoodie, etc.) per niche
              </li>
              <li>
                <strong>Recipe:</strong> Assign a custom recipe for tailored AI generation
              </li>
              <li>Settings persist and are used when generating from that niche</li>
            </ul>
          </div>

          <div className="bg-red-50 rounded-lg p-4">
            <h3 className="font-medium text-red-900 mb-2 flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Deleting Niches
            </h3>
            <p className="text-sm text-red-800">
              Click the trash icon on any niche to permanently delete it. This removes the niche and
              its research data but does not affect listings already generated from it.
            </p>
          </div>
        </div>
      </section>

      {/* Batch Generation */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-etsy-orange" />
          Batch Generation
        </h2>
        <p className="text-gray-600 mb-4">
          Generate multiple product variants at once from a single niche for faster workflow.
        </p>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Batch Strategies</h3>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-2">
              <li>
                <strong>Different Concepts + Styles:</strong> Most variety - each variant gets a
                unique concept and style combination
              </li>
              <li>
                <strong>Same Concept, Different Styles:</strong> One design idea rendered in
                multiple art styles
              </li>
              <li>
                <strong>Different Concepts, Same Style:</strong> Multiple ideas in one consistent
                style
              </li>
              <li>
                <strong>Matrix:</strong> Cross-product of all concepts with all selected styles
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">How It Works</h3>
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-2">
              <li>Select a niche from the Research page</li>
              <li>Choose batch strategy and variant count (1-20)</li>
              <li>Select art styles to include</li>
              <li>AI generates concepts based on niche analysis</li>
              <li>Preview images are generated for each variant</li>
              <li>Review and save variants as listings</li>
            </ol>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <h3 className="font-medium text-purple-900 mb-2">Analysis Influence</h3>
            <p className="text-sm text-purple-800">
              If niche research data is available, batch generation uses it to create more targeted
              designs. The AI considers target audience, market opportunities, demand signals, and
              price positioning when generating concepts.
            </p>
          </div>
        </div>
      </section>

      {/* Regenerate Design */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-etsy-orange" />
          Regenerating Designs
        </h2>
        <p className="text-gray-600 mb-4">
          Not happy with a design? Use the Regenerate button to create a new one.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <Image className="w-4 h-4" />
              Wall Art Mode
            </h3>
            <p className="text-sm text-gray-600">
              Generates complete printable artwork. Best for digital downloads and posters.
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <Shirt className="w-4 h-4" />
              Apparel Mode
            </h3>
            <p className="text-sm text-gray-600">
              Generates flat graphic designs optimized for printing on t-shirts, hoodies, etc.
            </p>
          </div>
        </div>

        <div className="mt-4 bg-yellow-50 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> The app auto-detects apparel products from the title (e.g.,
            "sweatshirt", "hoodie") and pre-selects the appropriate mode.
          </p>
        </div>
      </section>

      {/* Printify Integration */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-etsy-orange" />
          Printify Integration (Print-on-Demand)
        </h2>
        <p className="text-gray-600 mb-4">
          Sell physical products without inventory using Printify's print-on-demand service.
        </p>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">How It Works</h3>
            <ol className="text-sm text-gray-600 list-decimal list-inside space-y-2">
              <li>Select a Printify product template (e.g., Gildan Sweatshirt)</li>
              <li>Your design is uploaded to Printify</li>
              <li>Product is created as a draft in Printify</li>
              <li>Publish from Printify to your connected Etsy shop</li>
              <li>When customers order, Printify prints and ships automatically</li>
            </ol>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Available Products</h3>
            <p className="text-sm text-gray-600 mb-2">
              Configure available product types in Settings → Printify Config:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>T-Shirts (various brands)</li>
              <li>Hoodies & Sweatshirts</li>
              <li>Posters & Canvas prints</li>
              <li>And more...</li>
            </ul>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <h3 className="font-medium text-purple-900 mb-2">Variant Limits</h3>
            <p className="text-sm text-purple-800">
              Printify allows max 100 variants per product. EtsyFlow automatically filters to stay
              within this limit, prioritizing popular sizes and colors.
            </p>
          </div>
        </div>
      </section>

      {/* Managing Inventory */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5 text-etsy-orange" />
          Managing Inventory
        </h2>
        <p className="text-gray-600 mb-4">
          View, filter, and manage all your generated listings from the Inventory page.
        </p>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Filtering Options</h3>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>
                <strong>Product Type:</strong> Filter by Digital Downloads or POD products
              </li>
              <li>
                <strong>Status:</strong> Draft, Generating, Ready, Syncing, Published, Sync Failed
              </li>
              <li>
                <strong>View Mode:</strong> Switch between Grid and List views
              </li>
            </ul>
          </div>

          <div className="bg-red-50 rounded-lg p-4">
            <h3 className="font-medium text-red-900 mb-2 flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Deleting Listings
            </h3>
            <p className="text-sm text-red-800 mb-2">
              Click the trash icon on any listing to permanently delete it. In grid view, the delete
              button appears on hover.
            </p>
            <p className="text-sm text-red-800">
              <strong>Note:</strong> Deleting a listing removes it from EtsyFlow but does not
              automatically unpublish from Etsy or delete from Printify. Manage external platforms
              separately.
            </p>
          </div>
        </div>
      </section>

      {/* Recipes */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BookTemplate className="w-5 h-5 text-etsy-orange" />
          Recipes
        </h2>
        <p className="text-gray-600 mb-4">
          Recipes are saved configurations for quickly creating similar listings.
        </p>

        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-2">Recipe Includes</h3>
          <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
            <li>Target niche</li>
            <li>Product type (digital or POD)</li>
            <li>Printify blueprint & provider (for POD)</li>
            <li>Art style (minimalist, boho, vintage, etc.)</li>
            <li>AI models to use</li>
          </ul>
        </div>
      </section>

      {/* Art Styles */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5 text-etsy-orange" />
          Art Styles
        </h2>
        <p className="text-gray-600 mb-4">
          Choose from various art styles when generating designs:
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { name: 'Minimalist', desc: 'Clean lines, simple shapes' },
            { name: 'Boho', desc: 'Organic, earthy, bohemian' },
            { name: 'Vintage', desc: 'Retro, nostalgic feel' },
            { name: 'Modern', desc: 'Contemporary, sleek' },
            { name: 'Watercolor', desc: 'Soft, painted look' },
            { name: 'Botanical', desc: 'Plants, leaves, nature' },
            { name: 'Geometric', desc: 'Shapes, patterns' },
            { name: 'Abstract', desc: 'Non-representational art' },
            { name: 'Whimsical', desc: 'Playful, imaginative' },
            { name: 'Hand-drawn', desc: 'Sketch-like, organic' },
            { name: 'Typography', desc: 'Text-focused designs' },
            { name: 'Line Art', desc: 'Single line drawings' },
          ].map((style) => (
            <div key={style.name} className="p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-gray-900 text-sm">{style.name}</div>
              <div className="text-xs text-gray-500">{style.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Costs */}
      <section className="card mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-etsy-orange" />
          AI Costs
        </h2>
        <p className="text-gray-600 mb-4">
          All AI operations have costs tracked in the Costs page. Typical costs per listing:
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-medium text-gray-900">Operation</th>
                <th className="text-right py-2 font-medium text-gray-900">Approx. Cost</th>
              </tr>
            </thead>
            <tbody className="text-gray-600">
              <tr className="border-b">
                <td className="py-2">Content Generation (text)</td>
                <td className="text-right">~$0.01</td>
              </tr>
              <tr className="border-b">
                <td className="py-2">Design Generation (image)</td>
                <td className="text-right">~$0.04</td>
              </tr>
              <tr className="border-b">
                <td className="py-2">Mockup Generation (3 images)</td>
                <td className="text-right">~$0.12</td>
              </tr>
              <tr className="border-b">
                <td className="py-2">Research Analysis</td>
                <td className="text-right">~$0.01</td>
              </tr>
              <tr className="font-medium text-gray-900">
                <td className="py-2">Full Listing (with mockups)</td>
                <td className="text-right">~$0.17</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Workflow Summary */}
      <section className="card bg-gradient-to-br from-etsy-orange/10 to-orange-100">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Start Workflow</h2>
        <ol className="space-y-3">
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-etsy-orange text-white rounded-full flex items-center justify-center text-sm font-bold">
              1
            </span>
            <span className="text-gray-700">
              <strong>Research:</strong> Find a profitable niche with good demand and manageable
              competition
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-etsy-orange text-white rounded-full flex items-center justify-center text-sm font-bold">
              2
            </span>
            <span className="text-gray-700">
              <strong>Create:</strong> Generate a listing from the niche using full pipeline or
              recipe
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-etsy-orange text-white rounded-full flex items-center justify-center text-sm font-bold">
              3
            </span>
            <span className="text-gray-700">
              <strong>Review:</strong> Check the generated content, regenerate design if needed
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-etsy-orange text-white rounded-full flex items-center justify-center text-sm font-bold">
              4
            </span>
            <span className="text-gray-700">
              <strong>Publish:</strong> Sync to Etsy (digital) or create Printify draft (POD)
            </span>
          </li>
        </ol>
      </section>
    </div>
  );
}
